import React, {ReactNode} from 'react'
import {CurrentItemOvernight, OvernightFacilityDailyStatus, StatusInOneDay} from '../../AdminReserveSelectFacilityPage'
import { Table, TableBody, TableCell, TableRow, Box } from '@mui/material';
import {TableRowName} from '../../../../../styledComponents/adminPage/Table'
import PPTextField from '../../../../shared/forms/PPTextField'
import {formatDateWithDayOfTheWeek, formatDayOfTheWeek, formatMonthAndDay} from '../../../../../formatters/formatMoment'
import PPSelect from '../../../../shared/forms/PPSelect'
import {NUMBER_OF_TIMES_TO_STAY, RESERVATION_TYPE} from '../../../../../CodeMaster'
import {StatusType} from './ARReservationStatusViewOvernight'
import styled, {css} from 'styled-components'
import {ColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import FormErrorMessage from '../../../../shared/forms/FormErrorMessage'
import {Nullable, Optional} from '../../../../../app/TypeHelpers'
import {FitTableCell, Section} from './ARStyledComponents'
import {ReservationClass} from '../../../ReservationInterfaces'
import {Messages} from '../../../../../Messages'
import {SelectChangeEvent} from '@mui/material/Select'

interface Props {
    classification: ReservationClass
    facilityWithAvailability: OvernightFacilityDailyStatus
    reservationTitle: string
    reservationType: Nullable<RESERVATION_TYPE>
    currentItem: CurrentItemOvernight
    onChangeReservationTitle: (title: string) => void
    onChangeStayCount: (stayCount: NUMBER_OF_TIMES_TO_STAY) => void
}

export const ARSelectFacilityInputFormOvernight: React.FC<Props> = props => {
    const {
        classification,
        facilityWithAvailability,
        reservationTitle,
        reservationType,
        currentItem,
    } = props

    const {name, dailyStatus, stayPossibleDateCount} = facilityWithAvailability

    if (!dailyStatus) {
        throw new Error('dailyStatus is undefined')
    }

    const handleChangeReservationTitle = (event: React.ChangeEvent<HTMLInputElement>): void => {
        props.onChangeReservationTitle(event.target.value)
    }

    const handleChangeStayCount = (event: SelectChangeEvent<unknown>): void => {
        props.onChangeStayCount(NUMBER_OF_TIMES_TO_STAY.codeToEnumConstants(event.target.value as string))
    }

    const errorMessage = checkCurrentStatusAndConvertToMessage(dailyStatus[0].status, classification)

    const renderReserveAvailableTable = (statusList: StatusInOneDay[]) => {
        return <DateTable>
            <TableBody>
                <TableRow>
                    {
                        statusList.map(item => {
                            return <DateTableDayOfWeekCell align={'center'} key={`1-${item.date.date()}`}>
                                {formatDayOfTheWeek(item.date)}
                            </DateTableDayOfWeekCell>
                        })
                    }
                </TableRow>
                <TableRow>
                    {
                        statusList.map(item => {
                            return <DateTableDataCell align={'center'} key={`2-${item.date.date()}`}>
                                <ColumnFlexContainer>
                                    <div>{formatMonthAndDay(item.date)}</div>
                                    <Box mt={'2px'}>
                                        {convertToStatusNode(item.status)}
                                    </Box>
                                </ColumnFlexContainer>
                            </DateTableDataCell>
                        })
                    }
                </TableRow>
            </TableBody>
        </DateTable>
    }

    return <>
        <Table>
            <TableBody>
                <TableRow>
                    <FitTableCell colSpan={2}>
                        <Section>{name}</Section>
                        <Box mt={'10px'}>
                            {renderReserveAvailableTable(dailyStatus)}
                        </Box>
                    </FitTableCell>
                </TableRow>
                <TableRow>
                    <TableRowName>利用日</TableRowName>
                    <TableCell>
                        <div>{formatDateWithDayOfTheWeek(currentItem.startDate)}</div>
                        <FormErrorMessage messages={errorMessage ? [{message: errorMessage}] : undefined}/>
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableRowName>宿泊数</TableRowName>
                    <TableCell>
                        <PPSelect
                            data={NUMBER_OF_TIMES_TO_STAY.values()
                                .filter(item => Number(item.value) <= stayPossibleDateCount)
                                .map(item => {
                                    return {value: item.value, label: `${item.label}泊`}
                                })}
                            value={currentItem.stayCount.value}
                            onChange={handleChangeStayCount}
                        />
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableRowName>予約タイトル</TableRowName>
                    <TableCell>
                        <PPTextField
                            fullWidth
                            onChange={handleChangeReservationTitle}
                            value={reservationTitle}
                            InputProps={{
                                inputProps: {
                                    maxLength: 30,
                                },
                            }}
                        />

                        {classification === ReservationClass.MANAGER &&
                        <div>{Messages.IFACA0039.toString()}</div>
                        }
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableRowName>予約種別</TableRowName>
                    <TableCell>
                        {reservationType ? reservationType.label : '-'}
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    </>
}

const convertToStatusNode = (status: StatusType): ReactNode => {
    switch (status) {
        case 'otherReservation':
            return <StatusTag status={status}>
                <div>予約あり</div>
                <div>(予約不可)</div>
            </StatusTag>
        case 'blank':
            return <StatusTag status={status}>
                空きあり
            </StatusTag>
        case 'outsideHours':
            return <StatusTag status={status}>
                時間外
            </StatusTag>
        default:
            throw new Error('unknown status ' + status)
    }
}

const checkCurrentStatusAndConvertToMessage =
    (status: StatusType, classification: ReservationClass): Optional<string> => {
        if (classification === ReservationClass.AGENT ? status !== 'blank' : status === 'otherReservation') {
            return '指定した日付は予約できません。'
        }
        return undefined
    }

const DateTable = styled(Table)`
  border-top: 1px solid rgb(236, 236, 236);
  border-bottom: 1px solid rgb(236, 236, 236);
  border-left: 1px solid rgb(236, 236, 236);
`

const DateTableCell = styled(TableCell)`
  &.MuiTableCell-root {
    padding: 4px 2px 4px 2px;
`

const DateTableDayOfWeekCell = styled(DateTableCell)`
  border-right: 1px solid rgb(236, 236, 236);
  background-color: rgb(226, 229, 243);
`

const DateTableDataCell = styled(DateTableCell)`
  border-right: 1px solid rgb(236, 236, 236);
`

const StatusTag = styled.div<{ status: StatusType }>`
  font-size: 12px;
  display: table-cell;
  vertical-align: middle;
  line-height: 15px;
  height: 30px;
  width: 72px;
  white-space: nowrap;
  ${props => {
    if (props.status === 'otherReservation') {
      return css`
        color: white;
        background-color: rgb(112, 112, 112);
      `
    } else if (props.status === 'outsideHours') {
      return css`
        background-color: rgb(217, 217, 217);
      `
    }
  }}`
